import {
  TrainingProgram,
  CreateProgramInput,
  UpdateProgramInput,
  ProgramFilters,
  ProgramsResponse,
  ProgramOperationResult,
  ProgramsApiResponse,
  TrainingWeeksApiResponse,
  TrainingWeek,
  TrainingDayDetail,
  TrainingDayDetailApiResponse,
} from "../types/program";
import { ApiService } from "./apiService";
import { withIsActive, failure } from "./serviceHelpers";
import { fetchAllPages, unwrapPaginated } from "../utils/paginationHelper";
import { logger } from "../utils/logger";

// Search response interface
export interface ProgramSearchResponse {
  data: {
    current_page: number;
    data: TrainingProgram[];
    total: number;
    per_page: number;
    first_page_url: string;
    from: number;
    last_page: number;
    last_page_url: string;
    links: Array<{
      url: string | null;
      label: string;
      page: number | null;
      active: boolean;
    }>;
    next_page_url: string | null;
    path: string;
    prev_page_url: string | null;
    to: number;
  };
}

export class ProgramService {
  // CREATE - Add a new program
  static async createProgram(
    programData: CreateProgramInput
  ): Promise<ProgramOperationResult> {
    try {
      const response = await ApiService.post(
        "/training-programs/create-admin-program",
        programData
      );
      const result = await ApiService.handleResponse<{ data: TrainingProgram }>(
        response
      );

      if (result.data) {
        return { success: true, data: result.data };
      }

      logger.error("Unexpected create program API response format:", result);
      return { success: false, error: "Unexpected response format" };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }

  // READ - Get all programs with pagination and filters
  static async getPrograms(
    page: number = 1,
    filters: ProgramFilters = {}
  ): Promise<ProgramsResponse | null> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
      });

      if (filters.search) {
        params.append("search", filters.search);
      }

      if (filters.program_type) {
        params.append("program_type", filters.program_type);
      }

      if (filters.difficulty_level && filters.difficulty_level.length) {
        params.append(
          "difficulty_level",
          JSON.stringify(filters.difficulty_level)
        );
      }

      if (filters.challengeId) {
        params.append("challengeId", filters.challengeId.toString());
      }

      const response = await ApiService.get(
        `/admin/training-programs?${params.toString()}`
      );
      const result = await ApiService.handleResponse<ProgramsApiResponse>(
        response
      );

      const paged = unwrapPaginated(result, "programs");

      if (!paged) return null;

      return {
        programs: paged.items,

        total: paged.total,

        page: paged.page,

        limit: paged.limit,
      };
    } catch (err: any) {
      logger.error("Error fetching programs:", err);
      return null;
    }
  }

  // SEARCH - Search programs with pagination
  static async searchPrograms(
    searchTerm: string,
    page: number = 1,
    limit: number = 30,
    challengeId?: number
  ): Promise<ProgramSearchResponse | null> {
    try {
      const params = new URLSearchParams({
        searchTerm: searchTerm,
        page: page.toString(),
      });

      if (challengeId) {
        params.append("challengeId", challengeId.toString());
      }

      const response = await ApiService.get(
        `/training-programs/admin-search?${params.toString()}`
      );
      const result = await ApiService.handleResponse<ProgramSearchResponse>(
        response
      );

      return unwrapPaginated(result, "programs search") ? result : null;
    } catch (err: any) {
      logger.error("Error searching programs:", err);
      return null;
    }
  }

  // READ - Get all programs (without pagination)
  static async getAllPrograms(): Promise<TrainingProgram[]> {
    try {
      return await fetchAllPages<TrainingProgram>("/training-programs");
    } catch (err: any) {
      logger.error("Error fetching programs:", err);
      return [];
    }
  }

  // READ - Get a single program by ID (with users)
  static async getProgramById(
    programId: number
  ): Promise<TrainingProgram | null> {
    try {
      const response = await ApiService.get(`/training-programs/${programId}`);
      const result = await ApiService.handleResponse<{ data: TrainingProgram }>(
        response
      );

      if (result.data) {
        const program = result.data;
        if (program.users) {
          program.users = program.users.map(withIsActive);
        }
        return program;
      }

      logger.error("Unexpected program API response format:", result);
      return null;
    } catch (err: any) {
      logger.error("Error fetching program:", err);
      return null;
    }
  }

  // UPDATE - Update program information
  static async updateProgram(
    programId: number,
    updates: UpdateProgramInput
  ): Promise<ProgramOperationResult> {
    try {
      const response = await ApiService.patch(
        `/training-programs/${programId}`,
        updates
      );
      const result = await ApiService.handleResponse<{ data: TrainingProgram }>(
        response
      );

      if (result.data) {
        return { success: true, data: result.data };
      }

      logger.error("Unexpected update program API response format:", result);
      return { success: false, error: "Unexpected response format" };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }

  // DELETE - Delete program
  static async deleteProgram(
    programId: number
  ): Promise<ProgramOperationResult> {
    try {
      const response = await ApiService.delete(
        `/training-programs/${programId}`
      );
      await ApiService.handleResponse(response);

      return { success: true };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }

  // USERS - Get program with assigned users
  static async getProgramWithUsers(
    programId: number
  ): Promise<TrainingProgram | null> {
    try {
      const program = await this.getProgramById(programId);
      return program;
    } catch (err: any) {
      logger.error("Error fetching program with users:", err);
      return null;
    }
  }

  // USERS - Assign user to program
  static async assignUserToProgram(
    programId: number,
    userId: number
  ): Promise<ProgramOperationResult> {
    try {
      const response = await ApiService.post(
        `/training-programs/${programId}/assign-user/${userId}`,
        {}
      );
      const result = await ApiService.handleResponse<any>(response);

      return { success: true, data: result };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }

  // USERS - Unassign user from program
  static async unassignUserFromProgram(
    programId: number,
    userId: number
  ): Promise<ProgramOperationResult> {
    try {
      const response = await ApiService.delete(
        `/training-programs/${programId}/unassign-user/${userId}`
      );
      await ApiService.handleResponse(response);

      return { success: true };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }

  // EXERCISES - Assign exercise to program
  static async assignExerciseToProgram(
    programId: number,
    exerciseId: number
  ): Promise<ProgramOperationResult> {
    try {
      const response = await ApiService.post(
        `/training-programs/${programId}/assign-exercise/${exerciseId}`,
        {}
      );
      const result = await ApiService.handleResponse<any>(response);

      return { success: true, data: result };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }

  // EXERCISES - Unassign exercise from program
  static async unassignExerciseFromProgram(
    programId: number,
    exerciseId: number
  ): Promise<ProgramOperationResult> {
    try {
      const response = await ApiService.delete(
        `/training-programs/${programId}/unassign-exercise/${exerciseId}`
      );
      await ApiService.handleResponse(response);

      return { success: true };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }

  // EXERCISES - Get all exercises for assignment (exercises not assigned to this program)
  static async getAvailableExercisesForProgram(
    programId: number
  ): Promise<any[]> {
    try {
      // Get the program with current exercises
      const program = await this.getProgramById(programId);
      const assignedExerciseIds =
        program?.exercises?.map((exercise) => exercise.id) || [];

      // Get all exercises using pagination helper
      const allExercises = await fetchAllPages<any>("/exercises");

      // Filter out exercises already assigned to this program
      return allExercises.filter(
        (exercise: any) => !assignedExerciseIds.includes(exercise.id)
      );
    } catch (err: any) {
      logger.error("Error fetching available exercises for program:", err);
      return [];
    }
  }

  // USERS - Get all users for assignment (users not assigned to this program)
  static async getAvailableUsersForProgram(programId: number): Promise<any[]> {
    try {
      const program = await this.getProgramById(programId);
      const assignedUserIds = program?.users?.map((user) => user.id) || [];

      // Get all users using pagination helper
      const allUsers = await fetchAllPages<any>("/users");

      // Filter out users already assigned to this program
      return allUsers
        .filter(
          (user: any) => !assignedUserIds.includes(user.id) && !user.deleted_at
        )
        .map(withIsActive);
    } catch (err: any) {
      logger.error("Error fetching available users for program:", err);
      return [];
    }
  }

  // TRAINING WEEKS - Get training weeks for a program
  static async getTrainingWeeks(
    programId: number,
    page: number = 1,
    perPage: number = 15
  ): Promise<TrainingWeeksApiResponse | null> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });

      const response = await ApiService.get(
        `/training-programs/${programId}/training-weeks?${params.toString()}`
      );
      const result = await ApiService.handleResponse<TrainingWeeksApiResponse>(
        response
      );

      return unwrapPaginated(result, "training weeks") ? result : null;
    } catch (err: any) {
      logger.error("Error fetching training weeks:", err);
      return null;
    }
  }

  // TRAINING WEEKS - Get all training weeks for a program (without pagination)
  static async getAllTrainingWeeks(programId: number): Promise<TrainingWeek[]> {
    try {
      return await fetchAllPages<TrainingWeek>(
        `/training-programs/${programId}/training-weeks`
      );
    } catch (err: any) {
      logger.error("Error fetching all training weeks:", err);
      return [];
    }
  }

  // TRAINING DAY - Get training day details
  static async getTrainingDayDetail(
    programId: number,
    weekId: number,
    dayId: number
  ): Promise<TrainingDayDetail | null> {
    try {
      const response = await ApiService.get(
        `/training-programs/${programId}/training-weeks/${weekId}/training-days/${dayId}`
      );
      const result =
        await ApiService.handleResponse<TrainingDayDetailApiResponse>(response);

      if (result.data) {
        return result.data;
      } else {
        logger.error(
          "Unexpected training day detail API response format:",
          result
        );
        return null;
      }
    } catch (err: any) {
      logger.error("Error fetching training day detail:", err);
      return null;
    }
  }

  // Upload program image
  static async uploadProgramImage(
    programId: number,
    imageFile: File
  ): Promise<ProgramOperationResult> {
    try {
      const formData = new FormData();
      formData.append("file", imageFile);
      formData.append("trainingProgramId", programId.toString());

      // Goes through ApiService so an expired token is refreshed and the
      // upload retried; a raw fetch here used to make a 401 fatal.
      const result = await ApiService.postJson<any>(
        `/files/training-programs/${programId}/upload-program-image`,
        formData
      );

      return {
        success: true,
        data: result.data,
      };
    } catch (err) {
      return failure(err, "Failed to upload program image");
    }
  }

  // Delete program image
  static async deleteProgramImage(
    programId: number,
    fileId: number
  ): Promise<ProgramOperationResult> {
    try {
      const response = await ApiService.delete(`/files/delete/${fileId}`);
      await ApiService.handleResponse(response);

      return {
        success: true,
      };
    } catch (err) {
      return failure(err, "Failed to delete program image");
    }
  }

  // TRAINING DAY - Mass assign sets data (RIR, RPE, rest time) to all exercises in a training day
  static async massAssignSetsData(
    trainingDayId: number,
    data: {
      rpe?: number | null;
      rir?: number | null;
      rest_time_seconds?: number | null;
    }
  ): Promise<ProgramOperationResult> {
    try {
      const response = await ApiService.patch(
        `/training-days/${trainingDayId}/mass-assign-sets-data`,
        data
      );
      const result = await ApiService.handleResponse<any>(response);

      return {
        success: true,
        data: result.data,
      };
    } catch (err) {
      return failure(err, "Failed to mass assign sets data");
    }
  }
}
